import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Loader2, Plus, Search } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { usePermissions } from '@hooks/usePermissions';
import { useSelectedSocialAccount } from '@hooks/useSelectedSocialAccount';
import { useSocialPost } from '@contexts/SocialPostContext';
import socialService from '@services/socialService';
import type { Network, Pillar, PostStatus, SocialPost } from '@/types/social';
import { NETWORKS, NETWORK_LABELS, POST_STATUS_LABELS } from '@/types/social';
import { formatPostTime } from '@/utils/social';
import { PillarDot, PostCover, PostNetworks, StatusChip } from '@components/social/SocialBits';
import { ProfileSwitcher } from '@components/social/ProfileSwitcher';

/**
 * Todos os posts (lista com filtros) e ponto de entrada do compositor. `?post=ID` abre o post
 * (links do sino, da atividade e da biblioteca); `?media=ID` filtra os posts que usam a mídia.
 * Documentação: src/docs/redes-sociais/posts.md.
 */
export const SocialPostsView: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const mediaId = Number(params.get('media')) || undefined;
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<PostStatus | ''>('');
  const [network, setNetwork] = useState<Network | ''>('');
  const [pillarId, setPillarId] = useState('');
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const { openPost, openNewPost, version } = useSocialPost();
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('social.write').hasPermission;
  const { accounts, account, accountId, setAccountId, ready } = useSelectedSocialAccount();
  // Com um perfil escolhido, a rede é a dele.
  const effNetwork: Network | '' = account ? account.network : network;
  const accountPreset = account ? { account: { id: account.id, network: account.network } } : {};

  useEffect(() => {
    const id = Number(params.get('post'));
    if (id > 0) {
      openPost(id);
      params.delete('post');
      setParams(params, { replace: true });
    }
  }, [params, setParams, openPost]);

  useEffect(() => {
    socialService.listPillars().then(setPillars).catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    try {
      setPosts(
        await socialService.listPosts({
          search: query || undefined,
          status: status ? [status] : undefined,
          network: effNetwork,
          account_id: accountId ?? undefined,
          pillar_id: pillarId ? Number(pillarId) : undefined,
          media_id: mediaId,
        }),
      );
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [query, status, effNetwork, accountId, ready, pillarId, mediaId]);

  useEffect(() => {
    load();
  }, [load, version]);

  return (
    <div className="space-y-4">
      <ProfileSwitcher accounts={accounts} value={accountId} onChange={setAccountId} allowAll />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <form
          className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 w-full sm:w-auto"
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(search.trim());
          }}
        >
          <div className="relative col-span-2">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="input-field w-full sm:w-64 pl-8" placeholder="Título ou legenda" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="select-field w-full sm:w-44" value={status} onChange={(e) => setStatus(e.target.value as PostStatus | '')}>
            <option value="">Todos os status</option>
            {Object.entries(POST_STATUS_LABELS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
          <select
            className="select-field w-full sm:w-36"
            value={effNetwork}
            disabled={!!account}
            title={account ? 'A rede é a do perfil escolhido' : undefined}
            onChange={(e) => setNetwork(e.target.value as Network | '')}
          >
            <option value="">Todas as redes</option>
            {NETWORKS.map((n) => (
              <option key={n} value={n}>
                {NETWORK_LABELS[n]}
              </option>
            ))}
          </select>
          <select className="select-field w-full sm:w-36" value={pillarId} onChange={(e) => setPillarId(e.target.value)}>
            <option value="">Todos os pilares</option>
            {pillars.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <Button type="submit" size="sm" variant="outline" icon={<Search />}>
            Buscar
          </Button>
          {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
        </form>
        {canWrite && (
          <Button size="sm" icon={<Plus />} onClick={() => openNewPost(accountPreset)}>
            Novo post
          </Button>
        )}
      </div>

      {mediaId && (
        <div className="rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-sm text-sky-800 flex items-center justify-between">
          <span>Posts que usam a mídia #{mediaId}.</span>
          <button
            type="button"
            className="text-xs underline"
            onClick={() => {
              params.delete('media');
              setParams(params, { replace: true });
            }}
          >
            Ver todos
          </button>
        </div>
      )}

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {posts.map((p) => {
          const errors = p.issues.filter((i) => i.level === 'error').length;
          return (
            <button key={p.id} type="button" onClick={() => openPost(p.id)} className="card p-2 w-full flex gap-3 text-left">
              <PostCover post={p} className="w-16 h-20 rounded flex-shrink-0" />
              <span className="min-w-0 flex-1 space-y-1">
                <span className="block font-medium text-slate-800 truncate">{p.title}</span>
                <span className="flex flex-wrap items-center gap-2">
                  <StatusChip status={p.status} />
                  <PostNetworks post={p} />
                  {errors > 0 && p.status !== 'strategy' && (
                    <span className="inline-flex items-center gap-1 text-xs text-red-700">
                      <AlertTriangle size={12} /> {errors}
                    </span>
                  )}
                </span>
                <span className="block text-xs text-slate-500">
                  {formatPostTime(p.scheduled_at) || 'sem data'}
                  {p.owner_name && ` · ${p.owner_name}`}
                </span>
                <PillarDot color={p.pillar_color} name={p.pillar_name} />
              </span>
            </button>
          );
        })}
        {posts.length === 0 && !loading && <p className="text-center text-slate-500 py-8">Nenhum post encontrado.</p>}
      </div>

      <div className="card overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="table-header">
            <tr>
              <th className="w-14" />
              <th>Post</th>
              <th>Redes</th>
              <th>Status</th>
              <th>Data e hora</th>
              <th>Responsável</th>
              <th>Pilar</th>
              <th>Pendências</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => {
              const errors = p.issues.filter((i) => i.level === 'error').length;
              return (
                <tr key={p.id} className="table-row cursor-pointer" onClick={() => openPost(p.id)}>
                  <td className="table-cell">
                    <PostCover post={p} className="w-12 h-12 rounded" />
                  </td>
                  <td className="table-cell">
                    <p className="font-medium text-slate-800">{p.title}</p>
                    <p className="text-xs text-slate-500 line-clamp-1 max-w-md">{p.caption}</p>
                  </td>
                  <td className="table-cell">
                    <PostNetworks post={p} />
                  </td>
                  <td className="table-cell">
                    <StatusChip status={p.status} />
                  </td>
                  <td className="table-cell whitespace-nowrap">{formatPostTime(p.scheduled_at) || <span className="text-slate-400">sem data</span>}</td>
                  <td className="table-cell">{p.owner_name ?? '—'}</td>
                  <td className="table-cell">
                    <PillarDot color={p.pillar_color} name={p.pillar_name} />
                  </td>
                  <td className="table-cell">
                    {errors > 0 && p.status !== 'strategy' && (
                      <span className="inline-flex items-center gap-1 text-xs text-red-700">
                        <AlertTriangle size={12} /> {errors}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {posts.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="table-cell text-center text-slate-500 py-8">
                  Nenhum post encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};

export default SocialPostsView;
